import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { defineStyles } from "@/internal/emotion";
import { SenderProfileFormModal } from "@/pages/channels/pages/detail/email/SenderProfileFormModal";

const styles = defineStyles({
  root: css({
    paddingTop: 8,
  }),
  tableHeader: css({
    "& th": {
      fontWeight: 500,
      borderBottom: `1px solid ${theme.colors.staticFgLine}`,
    },
  }),
  iconSize: css({
    width: 16,
    height: 16,
  }),
});

const StatusBadge: React.FC<{
  status: InterludeTypes["SenderDomain"]["status"];
}> = ({ status }) => {
  const { t } = useTranslation();
  switch (status) {
    case "verified":
      return (
        <Badge variant="positive">
          {t("channels.senderDomain.status.verified")}
        </Badge>
      );
    case "warning":
      return (
        <Badge variant="attentive">
          {t("channels.senderDomain.status.warning")}
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="negative">
          {t("channels.senderDomain.status.failed")}
        </Badge>
      );
    default:
      status satisfies never;
      return null;
  }
};

const AuthBadge: React.FC<{ status: "pass" | "fail" }> = ({ status }) => {
  const { t } = useTranslation();
  return (
    <Badge variant={status === "pass" ? "positive" : "negative"}>
      {status === "pass"
        ? t("channels.senderDomain.auth.pass")
        : t("channels.senderDomain.auth.fail")}
    </Badge>
  );
};

const RecheckButton: React.FC<{
  channelId: number;
  domainId: number;
}> = ({ channelId, domainId }) => {
  const validateMutation = interlude.channel.useValidateSenderDomain({
    params: {
      channelId,
      domainId,
    },
  });

  return (
    <Button
      variant="transparent"
      onClick={() => validateMutation.mutate(undefined)}
      disabled={validateMutation.isPending}
    >
      {validateMutation.isPending ? (
        <CircularProgress size={16} css={styles.iconSize} />
      ) : (
        <MotifIcon un-i-motif="arrow_sync" css={styles.iconSize} />
      )}
    </Button>
  );
};

interface EmailSenderConfigurationTabProps {
  channelId: number;
  senderDomains: ReadonlyArray<InterludeTypes["SenderDomain"]>;
  senderProfiles: ReadonlyArray<InterludeTypes["SenderProfile"]>;
}

export const EmailSenderConfigurationTab: React.FC<EmailSenderConfigurationTabProps> =
  function EmailSenderConfigurationTab({
    channelId,
    senderDomains,
    senderProfiles,
  }) {
    const { t } = useTranslation();
    const [addModalOpen, toggleAddModalOpen] = useSwitch(false);
    const [editingProfileId, setEditingProfileId] = useState<number | null>(
      null,
    );
    const editingProfile = useMemo(
      () =>
        editingProfileId === null
          ? null
          : (senderProfiles.find((p) => p.id === editingProfileId) ?? null),
      [editingProfileId, senderProfiles],
    );
    const primarySenderDomain = senderDomains[0]?.domain ?? "";
    return (
      <Stack spacing="16px" css={styles.root}>
        <Stack spacing="8px">
          <Typography variant="body" fontWeight={600}>
            {t("channels.email.detail.section.senderDomain")}
          </Typography>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t("channels.email.detail.section.senderDomain.desc")}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead css={styles.tableHeader}>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      borderRight: `1px solid ${theme.colors.staticFgLine}`,
                    }}
                  >
                    <Typography variant="h3" style={{ fontSize: "14px" }}>
                      {t("channels.email.detail.table.column.senderDomain")}
                    </Typography>
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      borderRight: `1px solid ${theme.colors.staticFgLine}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing="4px"
                      sx={{ alignItems: "center" }}
                    >
                      <Typography variant="h3" style={{ fontSize: "14px" }}>
                        {t("channels.email.detail.table.column.overviewStatus")}
                      </Typography>
                      <Tooltip
                        title={t(
                          "channels.email.detail.table.column.overviewStatus.tooltip",
                        )}
                      >
                        <Box component="span" sx={{ display: "flex" }}>
                          <MotifIcon
                            un-i-motif="circle_info"
                            style={{
                              fontSize: 16,
                              color: theme.colors.staticFgNote,
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      borderRight: `1px solid ${theme.colors.staticFgLine}`,
                    }}
                  >
                    <Typography variant="h3" style={{ fontSize: "14px" }}>
                      {t("channels.email.detail.table.column.domainAuth")}
                    </Typography>
                  </TableCell>
                  <TableCell rowSpan={2}>
                    <Typography variant="h3" style={{ fontSize: "14px" }}>
                      {t("channels.email.detail.table.column.lastChecked")}
                    </Typography>
                  </TableCell>
                  <TableCell rowSpan={2} align="center">
                    <Typography variant="h3" style={{ fontSize: "14px" }}>
                      {t("channels.email.detail.table.column.recheck")}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      padding: 0,
                      borderLeft: `1px solid ${theme.colors.staticFgLine}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        "& > h3": {
                          flex: 1,
                          padding: "8px",
                          borderRight: `1px solid ${theme.colors.staticFgLine}`,
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                        },
                      }}
                    >
                      <Typography variant="h3" style={{ fontSize: "14px" }}>
                        CNAME
                      </Typography>
                      <Typography variant="h3" style={{ fontSize: "14px" }}>
                        DKIM1
                      </Typography>
                      <Typography variant="h3" style={{ fontSize: "14px" }}>
                        DKIM2
                      </Typography>
                      <Typography variant="h3" style={{ fontSize: "14px" }}>
                        DMARC
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    sx={{
                      borderBottom: `1px solid ${theme.colors.staticFgLine}`,
                    }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {senderDomains.map((domain) => (
                  <TableRow key={domain.id}>
                    <TableCell>
                      <Typography variant="body">{domain.domain}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={domain.status} />
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        sx={{
                          "& > div": {
                            flex: 1,
                            textAlign: "center",
                          },
                        }}
                      >
                        <Box>
                          <AuthBadge status={domain.mailCnameStatus} />
                        </Box>
                        <Box>
                          <AuthBadge status={domain.dkim1Status} />
                        </Box>
                        <Box>
                          <AuthBadge status={domain.dkim2Status} />
                        </Box>
                        <Box sx={{ borderRight: "none" }}>
                          <AuthBadge status={domain.dmarcStatus} />
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body">
                        {dayjs(domain.updatedAt).format("YYYY-MM-DD HH:mm")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <RecheckButton
                        channelId={channelId}
                        domainId={domain.id}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>

        <Stack spacing="8px">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            spacing="16px"
          >
            <Stack spacing="8px" sx={{ flex: 1 }}>
              <Typography variant="body" fontWeight={600}>
                {t("channels.email.detail.section.senderProfiles")}
              </Typography>
            </Stack>
            <Button variant="secondary" onClick={toggleAddModalOpen}>
              {t(
                "channels.email.detail.section.senderProfiles.add",
                "新增寄件設定檔",
              )}
            </Button>
          </Stack>
          <TableContainer>
            <Table>
              <TableHead css={styles.tableHeader}>
                <TableRow>
                  <TableCell>
                    <Typography variant="h3" style={{ fontSize: "14px" }}>
                      {t("channels.email.detail.table.column.fromName")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing="4px"
                      sx={{ alignItems: "center" }}
                    >
                      <Typography variant="h3" style={{ fontSize: "14px" }}>
                        {t("channels.email.detail.table.column.status")}
                      </Typography>
                      <Tooltip
                        title={t(
                          "channels.email.detail.table.column.status.tooltip",
                        )}
                      >
                        <Box component="span" sx={{ display: "flex" }}>
                          <MotifIcon
                            un-i-motif="circle_info"
                            style={{
                              fontSize: 16,
                              color: theme.colors.staticFgNote,
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {t("channels.email.detail.table.column.fromEmail")}
                  </TableCell>
                  <TableCell>
                    {t("channels.email.detail.table.column.replyTo")}
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {senderProfiles.map((profile) => (
                  <TableRow key={profile.id}>
                    <TableCell>
                      <Typography variant="body">{profile.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={profile.status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body">
                        {profile.fromEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body">{profile.replyTo}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={t(
                          "channels.email.detail.section.senderProfiles.edit",
                          "編輯",
                        )}
                      >
                        <Button
                          variant="transparent"
                          onClick={() => setEditingProfileId(profile.id)}
                        >
                          <MotifIcon un-i-motif="edit" css={styles.iconSize} />
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
        <SenderProfileFormModal
          open={addModalOpen}
          onClose={toggleAddModalOpen}
          channelId={channelId}
          senderDomain={primarySenderDomain}
          defaultReplyTo={senderProfiles[0]?.replyTo}
        />
        <SenderProfileFormModal
          open={editingProfile !== null}
          onClose={() => setEditingProfileId(null)}
          channelId={channelId}
          senderDomain={primarySenderDomain}
          editingProfile={
            editingProfile
              ? {
                  id: editingProfile.id,
                  name: editingProfile.name,
                  fromEmail: editingProfile.fromEmail,
                  replyTo: editingProfile.replyTo,
                }
              : undefined
          }
        />
      </Stack>
    );
  };
